// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Pressable, StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import Svg, { Circle, Line, Polyline } from 'react-native-svg';

import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';

const CHART_HEIGHT = 56;

/**
 * A line over the period's values. Deliberately unlabelled inside the plot — the current value
 * and the change are spelled out next to it, so the line only has to carry the shape.
 */
export function Sparkline({
  values,
  color = appColors.accent.freeze,
  /** Playing handicap and score-to-par are both "lower is better", so the axis is inverted. */
  invert = true,
  testID,
}: {
  values: number[];
  color?: string;
  invert?: boolean;
  testID?: string;
}) {
  const [width, setWidth] = useState(0);
  const onLayout = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width);

  if (values.length < 2 || width <= 0) {
    return <View style={styles.sparklineHost} onLayout={onLayout} testID={testID} />;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const padding = 6;
  const usableHeight = CHART_HEIGHT - padding * 2;
  const step = values.length > 1 ? (width - padding * 2) / (values.length - 1) : 0;

  const points = values.map((value, index) => {
    const ratio = (value - min) / span;
    const y = padding + (invert ? ratio : 1 - ratio) * usableHeight;
    return [padding + index * step, y] as const;
  });
  const last = points[points.length - 1];

  return (
    <View style={styles.sparklineHost} onLayout={onLayout} testID={testID}>
      <Svg width={width} height={CHART_HEIGHT}>
        <Line
          x1={0}
          y1={CHART_HEIGHT - 1}
          x2={width}
          y2={CHART_HEIGHT - 1}
          stroke={appColors.border.strong}
          strokeWidth={1}
        />
        <Polyline
          points={points.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <Circle cx={last[0]} cy={last[1]} r={3.5} fill={color} />
      </Svg>
    </View>
  );
}

export type FormBar = {
  key: string;
  /** Score to par for the round; lower is better. */
  value: number;
  label: string;
  best?: boolean;
};

/**
 * One bar per round, scaled against the period's worst score so the differences are visible.
 * Shorter is better, which matches "over par" reading downward.
 */
export function FormBars({
  bars,
  average,
  onSelect,
  bestLabel,
  averageLabel,
}: {
  bars: FormBar[];
  average: number | null;
  onSelect?: (key: string) => void;
  bestLabel: string;
  averageLabel: string;
}) {
  if (bars.length === 0) {
    return null;
  }

  const max = Math.max(...bars.map((bar) => bar.value), average ?? 0, 1);
  const min = Math.min(...bars.map((bar) => bar.value), average ?? 0, 0);
  const span = max - min || 1;
  const heightFor = (value: number) => Math.max(4, ((value - min) / span) * 72);

  return (
    <View style={styles.formWrap}>
      <View style={styles.formBars}>
        {bars.map((bar) => (
          <Pressable
            key={bar.key}
            style={styles.formBarColumn}
            onPress={onSelect ? () => onSelect(bar.key) : undefined}
            accessibilityRole={onSelect ? 'button' : undefined}
            accessibilityLabel={`${bar.label}: ${formatToPar(bar.value)}`}
          >
            <Text style={styles.formBarValue} numberOfLines={1}>
              {formatToPar(bar.value)}
            </Text>
            <View
              style={[
                styles.formBar,
                { height: heightFor(bar.value) },
                bar.best && styles.formBarBest,
              ]}
            />
          </Pressable>
        ))}
      </View>
      <View style={styles.formFooter}>
        <Text style={styles.formFooterText} numberOfLines={1}>
          {bars[0]?.label}
        </Text>
        <Text style={styles.formFooterText} numberOfLines={1}>
          {average !== null ? `${averageLabel} ${formatToPar(average)}` : ''}
        </Text>
        <Text style={styles.formFooterText} numberOfLines={1}>
          {bars.length > 1 ? bars[bars.length - 1]?.label : ''}
        </Text>
      </View>
      <Text style={styles.formLegend}>{bestLabel}</Text>
    </View>
  );
}

/**
 * A signed score-to-par: `E` at level par, otherwise `+n` / `−n`. Per-hole averages sit within
 * a stroke of each other, so those pass `decimals: 2` — at one decimal the par 3/4/5 comparison
 * collapses into the same number.
 */
export function formatToPar(value: number, decimals = 1): string {
  const factor = 10 ** decimals;
  const rounded = Math.round(value * factor) / factor;
  if (rounded === 0) return 'E';
  return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
}

const styles = StyleSheet.create({
  sparklineHost: {
    flex: 1,
    minWidth: 0,
    height: CHART_HEIGHT,
    justifyContent: 'center',
  },
  formWrap: {
    gap: 6,
  },
  formBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 4,
    minHeight: 92,
  },
  formBarColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 3,
  },
  formBarValue: {
    fontSize: 9.5,
    color: appColors.text.muted,
  },
  formBar: {
    width: '100%',
    maxWidth: 26,
    borderRadius: 3,
    backgroundColor: 'rgba(161,212,147,0.55)',
  },
  formBarBest: {
    backgroundColor: appColors.accent.primary,
  },
  formFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  formFooterText: {
    fontSize: 10,
    color: appColors.text.muted,
    flexShrink: 1,
  },
  formLegend: {
    fontSize: 10.5,
    color: appColors.text.muted,
  },
});
